<template>
  <FormQuestion
    @back="$emit('back')"
    @next="$emit('next')"
    :header="$bct('Child') + ` - ${childData.name}`"
    :hide-actions="hideActions"
    :next-disabled="nextDisabled"
    :processing="processing"
    :subtitle="subsidyProgram.preference_subtitle"
    :title="subsidyProgram.preference_title"
  >
    <v-stepper-vertical
      v-model="step"
      class="pb-0 full-width"
      flat
      hide-actions
      tile
    >
      <v-card
        class="mb-4 w-100pc"
        border
        flat
        tile
      >
        <v-card-text class="py-0 px-0">
          <v-stepper-vertical-item
            :complete="step > 1"
            value="1"
          >
            <template #title>
              <h3>{{ $bct(subsidyProgram.select_providers_title) }}</h3>
              <h4 class="fs-16 c-light-black">
                {{ $bct(subsidyProgram.select_providers_subtitle) }}
              </h4>
            </template>

            <SelectProvidersStep
              @copy="copyPreferences"
              @next="handleNext(1)"
              @open="openProvider($event)"
              @toggle="toggleSelection($event[0], $event[1])"
              :child-data="childData"
              :default-query="defaultQuery"
              :previous-child-name="previousChildName"
              :previous-subsidy-provider-ids="previousSubsidyProviderIds"
              :processing="processing"
              :provider-ids="subsidy.provider_ids"
              :selected-providers="selectedProviders"
              :subsidy-program="subsidyProgram"
            />
          </v-stepper-vertical-item>
        </v-card-text>
      </v-card>

      <v-card
        v-if="subsidyProgram.allow_preference_order"
        class="mb-4 w-100pc"
        border
        flat
        tile
      >
        <v-card-text class="py-0 px-0">
          <v-stepper-vertical-item
            :complete="step > 2"
            value="2"
          >
            <template #title>
              <h3 tabindex="0">
                {{ $bct(subsidyProgram.rank_providers_title) }}
              </h3>
              <h4
                class="fs-16 c-light-black"
                tabindex="0"
              >
                {{ $bct(subsidyProgram.rank_providers_subtitle) }}
              </h4>
            </template>

            <RankProvidersStep
              @back="handleBack(2)"
              @move="reorderProviders($event[0], $event[0] + $event[1])"
              @next="handleNext(2)"
              @toggle="toggleInclusion($event[0], $event[1])"
              :provider-ids="subsidy.provider_ids"
              :subsidy-program="subsidyProgram"
            />
          </v-stepper-vertical-item>
        </v-card-text>
      </v-card>

      <template v-if="subsidyProgram.enable_current_provider_selection">
        <v-card
          class="mb-4 w-100pc"
          data-testid="currentEnrollmentStep"
          border
          flat
          tile
        >
          <v-card-text class="py-0 px-0">
            <v-stepper-vertical-item
              :complete="step > 3"
              value="3"
            >
              <template #title>
                <h3 tabindex="0">
                  {{ $bct(subsidyProgram.current_provider_title) }}
                </h3>
                <h4
                  class="fs-16 c-light-black"
                  tabindex="0"
                >
                  {{ $bct(subsidyProgram.current_provider_subtitle) }}
                </h4>
              </template>

              <CurrentEnrollmentStep
                v-model="subsidy.current_provider_id"
                @back="handleBack(3)"
                @na="currentProviderNotApplicableClicked"
                :na="currentProviderNotApplicable"
                :providers="selectedProviders"
                :subsidy-program="subsidyProgram"
                show-na
              />
            </v-stepper-vertical-item>
          </v-card-text>
        </v-card>
      </template>
    </v-stepper-vertical>

    <ContentDialog
      ref="providerDialog"
      :max-width="800"
    >
      <template #content="{ data }">
        <ProviderMatch
          v-if="data"
          :match="data"
          :outlined="false"
          :schema="providerSchema"
        />
      </template>
    </ContentDialog>
  </FormQuestion>
</template>

<script setup>
import { useStore } from 'vuex';
import { VStepperVertical, VStepperVerticalItem } from 'vuetify/labs/VStepperVertical';

import ContentDialog from '@/shared/components/ContentDialog.vue';
import CurrentEnrollmentStep from '@/parent/components/subsidy/CurrentEnrollmentStep.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import ProviderMatch from '@/shared/components/search/ProviderMatch.vue';
import RankProvidersStep from '@/parent/components/subsidy/RankProvidersStep.vue';
import SelectProvidersStep from '@/parent/components/subsidy/SelectProvidersStep.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api: Api } = useApi();
const eventBus = useEventBus();
const store = useStore();

const $bct = inject('translator');

const props = defineProps({
  childData: {
    type: Object,
    default: null,
  },
  defaultQuery: {
    type: String,
    default: null,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  initialStep: {
    type: Number,
    default: 1,
  },
  previousChildName: {
    type: String,
    default: null,
  },
  previousSubsidyProviderIds: {
    type: Array,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['back', 'next', 'selectedProviderIds']);

const subsidy = defineModel('modelValue', {
  required: true,
  type: Object,
});
const currentProviderNotApplicable = ref(false);

const providerSchema = computed(() => store.state.schemas[props.subsidyProgram.provider_schema_id]);
const step = ref(props.initialStep);
const selectedProviders = ref([]);
const providerDialog = ref(null);

const nextDisabled = computed(() => {
  if (subsidy.value.provider_ids.length === 0) return true;

  if (props.subsidyProgram.enable_current_provider_selection) {
    return step.value < 3;
  }

  return false;
});

onMounted(async () => {
  await loadSelectedProviders();
});

async function copyPreferences() {
  subsidy.value.provider_ids = JSON.parse(JSON.stringify(props.previousSubsidyProviderIds));
  await loadSelectedProviders();
}

function currentProviderNotApplicableClicked() {
  currentProviderNotApplicable.value = !currentProviderNotApplicable.value;

  if (currentProviderNotApplicable.value) {
    subsidy.value.current_provider_id = null;
  }
}

async function loadSelectedProviders() {
  if (subsidy.value.provider_ids.length === 0) return;
  const resp = await Api.publicApi.organization.provider.index({
    ids: subsidy.value.provider_ids,
    page_size: 200,
  });
  selectedProviders.value = resp.data;
}

function openProvider(providerId) {
  Api.publicApi.provider.get(providerId, (resp) => {
    providerDialog.value.open(resp.data);
  });
}

function reorderProviders(fromIndex, toIndex) {
  const element = subsidy.value.provider_ids[fromIndex];
  subsidy.value.provider_ids.splice(fromIndex, 1);
  subsidy.value.provider_ids.splice(toIndex, 0, element);
}

async function toggleInclusion(index, newVal) {
  if (newVal) {
    subsidy.value.provider_ids.push(subsidy.value.excluded_provider_ids.splice(index, 1)[0]);
  } else {
    subsidy.value.excluded_provider_ids.push(subsidy.value.provider_ids.splice(index, 1)[0]);
  }
  await loadSelectedProviders();
}

function handleNext(from) {
  if (from === 1) {
    if (props.subsidyProgram.allow_preference_order) {
      step.value = 2;
    } else if (props.subsidyProgram.enable_current_provider_selection) {
      step.value = 3;
    }
  } else if (from === 2) {
    step.value = 3;
  }
}

function handleBack(from) {
  if (from === 3) {
    if (props.subsidyProgram.allow_preference_order) {
      step.value = 2;
    } else {
      step.value = 1;
    }
  } else if (from === 2) {
    step.value = 1;
  }
}

async function toggleSelection(providerId, newVal) {
  if (newVal) {
    if (!subsidy.value.provider_ids.includes(providerId)) {
      subsidy.value.provider_ids.push(providerId);
    }
    if (
      props.subsidyProgram.selection_limit > 0 &&
      props.subsidyProgram.selection_limit === subsidy.value.provider_ids.length
    ) {
      eventBus.chime(
        'You reached your selection limit. Deselect another option if you want to add more.',
      );
    }
  } else if (subsidy.value.provider_ids.includes(providerId)) {
    subsidy.value.provider_ids.splice(
      subsidy.value.provider_ids.findIndex((id) => id === providerId),
      1,
    );
  }
  emit('selectedProviderIds');
  await loadSelectedProviders();
}
</script>

<style scoped>
/* Former global style; remove and fix padding if necessary */
.full-width.v-stepper .v-stepper-vertical-item .v-expansion-panel-text,
.v-stepper-vertical-item .v-expansion-panel-text__wrapper {
  padding: 0 !important;
  padding-inline-start: 0 !important;
}

@media (max-width: 600px) {
  .v-stepper-vertical-item :deep(.v-expansion-panel-text__wrapper),
  .v-stepper-vertical-item :deep(.v-expansion-panel-title) {
    padding-left: 8px;
    padding-right: 8px;
  }

  .v-stepper-vertical-item :deep(.v-expansion-panel-text) {
    padding-inline-start: 0;
  }
}
</style>
